import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Rocket, X } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import { fetchPaymentTypes } from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import { fetchColleagues } from "../../features/instructors/slice-extensions/instructors-slice-extension";
import { fetchBusinessProfile } from "../../features/business-profile/slice-extensions/business-profile-slice-extension";
import { PaymentType } from "../../features/payment-types/models/PaymentType";
import { Instructor } from "../../features/instructors/models/Instructor";
import { ROUTES } from "../../routes";
import OnboardingStepList, { OnboardingStep } from "./OnboardingStepList";

const DISMISSED_KEY = "tp-onboarding-fahrschule-dismissed";

export interface FahrschuleSetup {
  pricesFilled: boolean;
  licencesFilled: boolean;
  addressFilled: boolean;
}

/**
 * Prices are filled once no active Payment Type is still at 0 (the preset creates them
 * at 0); every active instructor needs an FL-Nr.; the school needs an address.
 */
export const toFahrschuleSetup = (
  paymentTypes: PaymentType[],
  instructors: Instructor[],
  address: string | null
): FahrschuleSetup => {
  const active = paymentTypes.filter((p) => p.isActive);
  const teaching = instructors.filter((i) => i.isActive);
  return {
    pricesFilled: active.length > 0 && active.every((p) => p.priceAmountMinorUnits > 0),
    licencesFilled: teaching.length > 0 && teaching.every((i) => (i.licenceNumber ?? "").trim() !== ""),
    addressFilled: (address ?? "").trim() !== "",
  };
};

/**
 * The home checklist of a Fahrschule: Preisaushang, FL-Nr. per instructor, school
 * address. Reads existing endpoints only; a failed probe hides the card this visit.
 */
const FahrschuleChecklist: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [setup, setSetup] = useState<FahrschuleSetup | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) != null);

  useEffect(() => {
    let active = true;
    Promise.all([
      dispatch(fetchPaymentTypes()).unwrap(),
      dispatch(fetchColleagues()).unwrap(),
      dispatch(fetchBusinessProfile()).unwrap(),
    ])
      .then(([paymentTypes, instructors, profile]) => {
        if (active) setSetup(toFahrschuleSetup(paymentTypes, instructors, profile.address));
      })
      .catch(() => {
        // A failed probe (e.g. an Instructor without billing access) just means no card this visit.
      });
    return () => {
      active = false;
    };
  }, [dispatch]);

  if (dismissed || !setup) return null;

  const steps: OnboardingStep[] = [
    {
      key: "prices", done: setup.pricesFilled, label: t("onboarding.fahrschule.prices"),
      hint: t("onboarding.fahrschule.pricesHint"), action: t("onboarding.fahrschule.pricesAction"),
      to: `${ROUTES.BILLING}?tab=payment-types`,
    },
    {
      key: "licence", done: setup.licencesFilled, label: t("onboarding.fahrschule.licence"),
      hint: t("onboarding.fahrschule.licenceHint"), action: t("onboarding.fahrschule.licenceAction"),
      to: ROUTES.SETTINGS,
    },
    {
      key: "address", done: setup.addressFilled, label: t("onboarding.fahrschule.address"),
      hint: t("onboarding.fahrschule.addressHint"), action: t("onboarding.fahrschule.addressAction"),
      to: ROUTES.ORGANIZATION_SETTINGS,
    },
  ];
  const allDone = steps.every((s) => s.done);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Rocket size={20} className="text-primary-40 dark:text-primary-90" />
          <h3 className="text-grey-10 dark:text-grey-90">
            {allDone ? t("onboarding.doneTitle") : t("onboarding.fahrschule.title")}
          </h3>
        </div>
        {allDone && (
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("onboarding.dismiss")}
            className="rounded-md p-1 text-grey-30 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
          >
            <X size={18} />
          </button>
        )}
      </div>
      <p className="mt-1 text-sm text-grey-30 dark:text-grey-70">{t("onboarding.fahrschule.subtitle")}</p>
      <OnboardingStepList steps={steps} />
    </div>
  );
};

export default FahrschuleChecklist;
